"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

type BulkTestStatus = "pending" | "success" | "error";

interface BulkTestContextValue {
  getStatus: (id: number) => BulkTestStatus | undefined;
  setBulkPending: (ids: number[]) => void;
  setBulkResults: (successfulIds: number[], failedIds: number[]) => void;
  clearStatuses: () => void;
}

const BulkTestContext = createContext<BulkTestContextValue | undefined>(
  undefined,
);

const AUTO_CLEAR_DELAY_MS = 5000;

export function BulkTestProvider({ children }: { children: React.ReactNode }) {
  const [statusMap, setStatusMap] = useState<Map<number, BulkTestStatus>>(
    () => new Map(),
  );
  const clearTimerReference = useRef<ReturnType<typeof setTimeout>>();

  const cancelPendingClear = useCallback(() => {
    if (clearTimerReference.current) {
      clearTimeout(clearTimerReference.current);
      clearTimerReference.current = undefined;
    }
  }, []);

  const clearStatuses = useCallback(() => {
    cancelPendingClear();
    setStatusMap(new Map());
  }, [cancelPendingClear]);

  const setBulkPending = useCallback(
    (ids: number[]) => {
      cancelPendingClear();
      setStatusMap(new Map(ids.map((id) => [id, "pending" as const])));
    },
    [cancelPendingClear],
  );

  const setBulkResults = useCallback(
    (successfulIds: number[], failedIds: number[]) => {
      setStatusMap((previous) => {
        const next = new Map(previous);
        for (const id of successfulIds) {
          next.set(id, "success");
        }
        for (const id of failedIds) {
          next.set(id, "error");
        }
        return next;
      });

      clearTimerReference.current = setTimeout(
        clearStatuses,
        AUTO_CLEAR_DELAY_MS,
      );
    },
    [clearStatuses],
  );

  const getStatus = useCallback((id: number) => statusMap.get(id), [statusMap]);

  useEffect(() => {
    return () => {
      if (clearTimerReference.current) {
        clearTimeout(clearTimerReference.current);
      }
    };
  }, []);

  const value = useMemo(
    () => ({ getStatus, setBulkPending, setBulkResults, clearStatuses }),
    [getStatus, setBulkPending, setBulkResults, clearStatuses],
  );

  return (
    <BulkTestContext.Provider value={value}>
      {children}
    </BulkTestContext.Provider>
  );
}

export function useBulkTestContext() {
  return useContext(BulkTestContext);
}
